import { useQuery, useMutation } from 'react-query'
import queryString from 'query-string'
import axios from 'axios'

import {
  CustomError,
  SessionError,
  InternalServerError,
  ForbiddenError,
  ServiceUnavailableError,
  NotFoundError,
  NetworkError,
} from 'utils/customError'
import AndroidHandler from 'utils/androidHandler'
import { setAuthToAPI, getSession } from 'utils/auth'
import { timeout } from 'utils/process'
import { APP_LOGIN_STORAGE_KEY } from 'configs/auth'
import { baseUrl } from 'configs/api'

import type { AxiosRequestConfig, AxiosPromise, AxiosError } from 'axios'
import type {
  UseQueryResult,
  UseQueryOptions,
  UseMutationOptions,
} from 'react-query'
import type { GuruTokenSession, Session } from 'types/auth'
import type { CustomError as ICustomError } from 'types/custom-error'

type CommonErrorHandlerFn = (
  status: any,
  path: string,
  error: any,
  errorResponse?: any
) => void

interface QueryApiConfig<TQueryFnData = unknown> {
  axiosConfig?: AxiosRequestConfig
  queryConfig?: UseQueryOptions<TQueryFnData>
  storageKey?: string
  storageSerializer?: (any) => any
  serviceUnavailable?: boolean
  errorHandlerFn?: CommonErrorHandlerFn
  /**
   * guru token for CCT is passed through query param
   */
  tokenFromQueryParam?: string | string[]
}

interface MutationApiConfig<
  TData = unknown,
  TError = unknown,
  TVariables = void
> {
  axiosConfig?: AxiosRequestConfig
  mutationConfig?: UseMutationOptions<TData, TError, TVariables>
  storageKey?: string
  storageSerializer?: (any) => any
  errorHandlerFn?: CommonErrorHandlerFn
  /**
   * guru token for CCT is passed through query param
   */
  tokenFromQueryParam?: string | string[]
}

let sessionError: ICustomError | null = null

export const handleCommonErrors = (status, path: string, error: any) => {
  const androidHandler = AndroidHandler()

  if (!androidHandler.enabled && error?.message === 'Network Error') {
    throw new NetworkError(path, error)
  }

  switch (status) {
    case 400:
      throw new InternalServerError(path, error)
    case 401:
      throw new SessionError(path, error)
    case 403:
      throw new ForbiddenError(path, error)
    case 404:
      throw new NotFoundError(path, error)
    case 500:
      throw new InternalServerError(path, error)
    case 503:
      throw new ServiceUnavailableError(path, error)
    default:
      throw error
  }
}

export function useQueryApi<TQueryFnData, TQueryFnError = AxiosError>(
  path: string,
  {
    axiosConfig = {},
    queryConfig = {},
    storageKey = APP_LOGIN_STORAGE_KEY,
    storageSerializer,
    serviceUnavailable = false,
    tokenFromQueryParam,
    errorHandlerFn = handleCommonErrors,
  }: QueryApiConfig<TQueryFnData> = {
    axiosConfig: {},
    queryConfig: {},
    storageKey: APP_LOGIN_STORAGE_KEY,
    storageSerializer: undefined,
    serviceUnavailable: false,
    tokenFromQueryParam: null,
  }
): UseQueryResult<TQueryFnData, TQueryFnError> {
  let queryKey: any = []

  if (!!axiosConfig.data) queryKey.push(axiosConfig.data)
  if (!!axiosConfig.params) queryKey.push(axiosConfig.params)
  if (queryKey.length > 0) {
    queryKey = [path, ...queryKey]
  } else {
    queryKey = path
  }

  return useQuery<TQueryFnData, TQueryFnError>(
    queryKey,
    (): Promise<TQueryFnData> => {
      return api<TQueryFnData>(
        path,
        axiosConfig,
        storageKey,
        serviceUnavailable,
        tokenFromQueryParam,
        storageSerializer,
        errorHandlerFn
      )
    },
    {
      useErrorBoundary: true,
      ...queryConfig,
    }
  )
}

export function useMutationApi<TData, TError, TVariables>(
  path: string | ((data) => string),
  {
    axiosConfig = {},
    mutationConfig = {},
    storageKey = APP_LOGIN_STORAGE_KEY,
    storageSerializer,
    tokenFromQueryParam,
    errorHandlerFn = handleCommonErrors,
  }: MutationApiConfig<TData, TError, TVariables> = {
    axiosConfig: {},
    mutationConfig: {},
    storageKey: APP_LOGIN_STORAGE_KEY,
    storageSerializer: undefined,
    tokenFromQueryParam: null,
  }
) {
  return useMutation<TData, TError, TVariables>((data: any): Promise<TData> => {
    const pathApi = typeof path === 'function' ? path(data) : path
    return api<TData>(
      pathApi,
      { ...axiosConfig, data },
      storageKey,
      false,
      tokenFromQueryParam,
      storageSerializer,
      errorHandlerFn
    )
  }, mutationConfig)
}

export const paramsSerializer = (params?: Record<string, any>): string =>
  queryString.stringify(params ?? {}, { arrayFormat: 'none' })

const guruTokenShallowValidation = (storageSerializer, storageKey, path) => {
  // only allow non empty token for now since there are user that use old version of apps
  // return !!token && token.indexOf('guru') === 0
  const session = storageSerializer(getSession(storageKey))
  // check if current session is using guru token and guru token is not empty and has guru prefix
  // if (
  //   session.hasOwnProperty('guruToken') &&
  //   (!session.guruToken || session.guruToken.indexOf('guru') !== 0)
  // ) {
  //   throw new SessionError(path, null)
  // }
  if (session.hasOwnProperty('guruToken') && !session.guruToken) {
    throw new SessionError(path, null)
  }
}

export async function api<T>(
  path: string,
  options: AxiosRequestConfig = {},
  storageKey: string = APP_LOGIN_STORAGE_KEY,
  serviceUnavailable: boolean = false,
  tokenFromQueryParam: string | string[] | undefined = undefined,
  storageSerializer: (any) => any = (param) => param,
  errorHandlerFn: CommonErrorHandlerFn = handleCommonErrors
): Promise<T> {
  const androidHandler = AndroidHandler()
  const requestApi = (): AxiosPromise<T> => {
    const url = `${baseUrl}${path}`

    if (tokenFromQueryParam) {
      options.headers = {
        Authorization: `Bearer ${tokenFromQueryParam}`,
      }
    } else {
      const session = storageSerializer(getSession(storageKey))
      options.headers = {
        Authorization: `Bearer ${session.guruToken || session.accessToken}`,
      }
    }

    return axios({
      paramsSerializer: { serialize: paramsSerializer },
      url,
      ...(options ?? {}),
    })
  }

  if (serviceUnavailable) {
    throw new ServiceUnavailableError(path, null)
  }

  try {
    if (!tokenFromQueryParam) {
      const refreshSession = androidHandler.enabled
        ? refreshSessionApp
        : refreshSessionWeb
      await refreshSession(storageKey, storageSerializer)
      guruTokenShallowValidation(storageSerializer, storageKey, path)
    }
    const response = await requestApi()
    return response.data
  } catch (error) {
    // eslint-disable-next-line
    console.log(`api-error: ${path}`, JSON.stringify(error?.response))

    // Force logout when session expired / un-refresh-able
    if (error instanceof SessionError && !sessionError) {
      sessionError = error

      if (androidHandler.enabled) {
        androidHandler.requestLogin()
      } else {
        const pathname = window.location.pathname || '/home'
        window.location.href = `/logout?from=${pathname}&target=/login&error=SessionError`
      }
    }

    if (error instanceof CustomError) {
      throw error
    }

    // save navigation because error type object can be other than AxiosError
    const status = error?.response?.status
    errorHandlerFn(status, path, error, error.response)
  }
}

let refreshTokenPromise: AxiosPromise<Session> | null

export async function refreshSessionWeb(
  storageKey: string,
  storageSerializer: (any) => any = (param) => param
): Promise<Session> {
  const session = storageSerializer(getSession(storageKey))
  const isGuruToken = !!session.guruToken
  const expiryTime = isGuruToken ? session.expiredAt : session.expiryToken
  const sessionExpiryTime = new Date(expiryTime).getTime()
  const currentTime = new Date().getTime()
  const isTokenExpired = sessionExpiryTime < currentTime

  const path = isGuruToken
    ? '/teachers/v1alpha2/guru-token/refresh'
    : '/teachers/v1alpha1/profile/refresh_token'

  guruTokenShallowValidation(storageSerializer, storageKey, null)

  try {
    if (isTokenExpired) {
      const apiUrl = baseUrl + path

      let data

      if (!refreshTokenPromise) {
        const guruTokenAxiosConfig: AxiosRequestConfig = {
          url: apiUrl,
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.guruToken}`,
          },
        }
        const accessTokenAxiosConfig: AxiosRequestConfig = {
          url: setAuthToAPI(apiUrl, session),
          method: 'GET',
        }
        const axiosConfig: AxiosRequestConfig = isGuruToken
          ? guruTokenAxiosConfig
          : accessTokenAxiosConfig
        refreshTokenPromise = axios(axiosConfig) as AxiosPromise<Session>
      }
      data = (await refreshTokenPromise).data
      data = data.data || data
      window.localStorage.setItem(storageKey, JSON.stringify(data))
      refreshTokenPromise = null
      return data
    } else {
      return session
    }
  } catch (error) {
    refreshTokenPromise = null
    throw new SessionError(path, error)
  }
}

export async function refreshSessionApp(
  storageKey: string,
  storageSerializer: (any) => any = (param) => param
): Promise<Session> {
  const androidHandler = AndroidHandler()

  return androidHandler.guruTokenEnabled
    ? refreshGuruTokenSessionApp(storageKey, storageSerializer)
    : refreshAccessTokenSessionApp(storageKey, storageSerializer)
}

let refreshAppSessionPromise: Promise<GuruTokenSession> | null

export async function refreshGuruTokenSessionApp(
  storageKey: string,
  storageSerializer: (any) => any = (param) => param
): Promise<Session> {
  const session = storageSerializer(getSession(storageKey))
  const sessionExpiryTime = new Date(session.expiredAt).getTime()
  const currentTime = new Date().getTime()
  const isTokenExpired = sessionExpiryTime < currentTime

  try {
    if (isTokenExpired) {
      const androidHandler = AndroidHandler()
      let data: GuruTokenSession

      if (!refreshAppSessionPromise) {
        refreshAppSessionPromise = androidHandler.getAuthData(true)
      }
      data = await refreshAppSessionPromise

      const newSession: Session = {
        accessToken: '',
        tokenType: 'Bearer',
        expiryToken: data.expiredAt,
        refreshToken: '',
        email: data.user.email,
        expiredAt: data.expiredAt,
        guruToken: data.guruToken,
        user: data.user,
      }
      window.localStorage.setItem(
        APP_LOGIN_STORAGE_KEY,
        JSON.stringify(newSession)
      )
      return newSession
    } else {
      return session
    }
  } catch (error) {
    refreshAppSessionPromise = null
    throw new SessionError('app-refresh-session-error', error)
  }
}

export async function refreshAccessTokenSessionApp(
  storageKey: string,
  storageSerializer: (any) => any = (param) => param
): Promise<Session> {
  const session = storageSerializer(getSession(storageKey))
  const sessionExpiryTime = new Date(session.expiryToken).getTime()
  const currentTime = new Date().getTime()
  const isTokenExpired = sessionExpiryTime < currentTime

  if (isTokenExpired) {
    const androidHandler = AndroidHandler()
    androidHandler.refreshToken()
    await timeout(1000)
    const accessToken = androidHandler.requestAccessToken()
    const expiredAt = new Date(new Date().getTime() + 3600 * 1000).toISOString()
    const user = JSON.parse(androidHandler.getUser())
    const newSession: Session = {
      accessToken,
      tokenType: 'Bearer',
      expiryToken: expiredAt,
      refreshToken: 'dummy-refresh-token',
      email: androidHandler.getEmail(),
      expiredAt: expiredAt,
      guruToken: '',
      user: {
        id: user.id,
        name: user.name,
        email: user.email,
        picture: user.photoUrl,
        groups: user.groups,
      },
    }
    window.localStorage.setItem(
      APP_LOGIN_STORAGE_KEY,
      JSON.stringify(newSession)
    )
    return newSession
  }
  return session
}
